'use client';

import { Response } from '@frontend/client/clients/ratesClient';
import { config } from '@frontend/client/hooks/config';
import {
  isNativeToken,
  normalizeBigInt,
  SUPPORTED_CHAIN_IDS,
  SUPPORTED_CHAINS,
  SupportedChainId,
} from '@frontend/common';
import { buildContractCallForBalance } from '@frontend/utils/ercHelpers';
import { useQuery } from '@tanstack/react-query';
import { getBalance, multicall } from '@wagmi/core';
import { TokenInfo, tokenlist } from '@yodlpay/tokenlists';
import BigNumber from 'bignumber.js';
import _, { uniq } from 'lodash';
import { useMemo } from 'react';
import { Address } from 'viem';

export type UserBalance = {
  key: string;
  token: TokenInfo;
  chainId: number;
  value: bigint;
  decimals: number;
  valueUSD?: bigint;
};

interface NativeProps {
  chainIds?: SupportedChainId[];
  address?: Address;
}

interface AllBalancesProps {
  address?: Address;
  chainIds?: SupportedChainId[];
  filterFn?: ((balance: UserBalance) => boolean) | null;
}

const useRates = () => {
  const tokens = uniq(tokenlist.map((t) => t.coinGeckoId).filter(Boolean)).join(
    ',',
  );

  return useQuery({
    queryKey: ['rates', tokens],
    queryFn: async () => {
      const resp = await fetch(`/api/rates?symbols=${tokens}`);
      return resp.json() as Promise<Response['get /v1/rates']>;
    },
  });
};

const useNativeBalances = ({ chainIds, address }: NativeProps) => {
  const query = useQuery({
    queryKey: ['userNativeBalances', address, chainIds],
    queryFn: async () => {
      if (!chainIds || !address) return [];

      const promises = chainIds.map(async (chainId) => {
        const nativeToken = getNativeTokenInfo(chainId);
        if (!nativeToken || !chainId) return undefined;

        const resp = await getBalance(config, {
          address,
          chainId: chainId as SupportedChainId,
        });

        return {
          value: resp.value,
          decimals: resp.decimals,
          chainId,
          token: nativeToken,
          key: `${chainId}-native`,
        } as UserBalance;
      });

      return Promise.allSettled(promises).then((results) =>
        _.compact(
          results.filter((r) => r.status === 'fulfilled').map((r) => r.value),
        ),
      );
    },
  });

  return query;
};

const useTokenBalances = ({ address }) => {
  const query = useQuery({
    queryKey: ['senderBalances', address],
    queryFn: async () => {
      if (!address) return [];

      const promises = SUPPORTED_CHAINS.map(async (chain) => {
        const chainId = chain.id;
        const tokensOnChain = tokenlist.filter((t) => t.chainId === chainId);

        const contracts = tokensOnChain.map((token) => {
          return buildContractCallForBalance(token.address, chainId, address);
        });

        const resp = await multicall(config, { contracts, chainId });

        // Do not filter first, it will mess ip the index/order
        return resp
          .map((data, index) => {
            if (data.status != 'success') return null;
            const value = BigInt(data.result);
            const token = tokensOnChain[index];
            const { decimals, address } = token;

            return {
              value,
              decimals,
              key: `${chainId}-${address}`,
              token,
              chainId,
            } as UserBalance;
          })
          .filter((item): item is UserBalance => item !== null);
      });

      // TODO: consider using Promise.allSettled. if one chain fails, we still want to return the balances for the other chains
      //       possibly with a warning that one or more chains failed.
      return Promise.all(promises).then((results) => results.flat());
    },
  });

  const invalidateAndRefetch = () => {
    query.refetch();
  };

  return { ...query, invalidateAndRefetch };
};

export const useBalancesForToken = ({
  address,
  tokenSymbol,
}: {
  address: Address | undefined;
  tokenSymbol: string | undefined;
}) => {
  const { allBalances } = useAllBalances({
    address: address,
  });

  const filteredBalances = useMemo(() => {
    return allBalances?.filter(
      (balance) => balance.token.symbol === tokenSymbol,
    );
  }, [allBalances, tokenSymbol]);

  // Calculate totals in USD
  const totalValueUSD =
    filteredBalances?.reduce((acc, balance) => {
      return acc + (balance.valueUSD || 0n);
    }, 0n) || 0n;

  const maxValueUSD =
    filteredBalances?.reduce((max, balance) => {
      const balanceUSD = balance.valueUSD || 0n;
      return balanceUSD > max ? balanceUSD : max;
    }, 0n) || 0n;

  const total = { value: totalValueUSD, decimals: 6 };
  const max = { value: maxValueUSD, decimals: 6 };

  return {
    total,
    max,
  };
};

/**
 * Combines useSenderBalances and useNativeBalances.
 *
 * Returns all balances for a given address, including both ERC20 and native tokens.
 * @param address - The address to get balances for.
 * @param chainIds - The chain IDs to get balances for.
 * @param filterFn - A function to filter the balances.
 * @returns An object containing all balances, native balances, token balances, and loading status.
 */
export const useAllBalances = ({
  address,
  chainIds = SUPPORTED_CHAIN_IDS,
  filterFn = null,
}: AllBalancesProps) => {
  const {
    data: tokenBalances,
    isLoading: isLoadingTokenBalances,
    isSuccess: isSuccessTokenBalances,
    isFetched: isFetchedTokenBalances,
  } = useTokenBalances({ address });

  const {
    data: nativeBalances,
    isLoading: isLoadingNativeBalances,
    isSuccess: isSuccessNativeBalances,
    isFetched: isFetchedNativeBalances,
  } = useNativeBalances({
    chainIds,
    address,
  });

  const {
    data: rates,
    isLoading: isLoadingRates,
    isSuccess: isSuccessRates,
  } = useRates();

  const {
    data: allBalances,
    isLoading: isLoadingAllBalances,
    isSuccess: isSuccessAllBalances,
  } = useQuery({
    queryKey: ['allBalances', tokenBalances, nativeBalances, filterFn, rates],
    queryFn: async () => {
      const withBalance = (balances: UserBalance[] = []) =>
        balances.filter((b) => b.value > 0n);

      const merged = [
        ...withBalance(tokenBalances),
        ...withBalance(nativeBalances),
      ];

      if (!rates || rates.status !== 'success')
        return filterFn ? merged.filter(filterFn) : merged;

      for (const balance of merged) {
        const id = balance.token?.coinGeckoId;
        const rate = id ? (rates.data.rates[id] ?? 0) : 0;

        const normalizedValue =
          normalizeBigInt(balance.value, balance.decimals) * rate;

        balance.valueUSD = BigInt(
          new BigNumber(normalizedValue).multipliedBy(1e6).toFixed(0),
        );
      }

      return filterFn ? merged.filter(filterFn) : merged;
    },
    enabled: isFetchedTokenBalances && isFetchedNativeBalances,
  });

  return {
    allBalances,
    nativeBalances,
    tokenBalances,
    isLoading:
      isLoadingTokenBalances ||
      isLoadingNativeBalances ||
      isLoadingAllBalances ||
      isLoadingRates,
    isSuccess:
      isSuccessTokenBalances &&
      isSuccessNativeBalances &&
      isSuccessAllBalances &&
      isSuccessRates,
  };
};

function getNativeTokenInfo(chainId: number): TokenInfo | undefined {
  const nativeToken = tokenlist.find(
    (token) => token.chainId === chainId && isNativeToken(token.address),
  );
  return nativeToken as TokenInfo;
}
